/**
 * Shell auth service — Sprint 02 W-1.
 *
 * Single source of truth for the signed-in user's identity. Calls
 * `GET /api/auth/me` once per React Query cache window; the Shell
 * renders `<LoginScreen>` when this returns a 401 and the full nav
 * when it returns a user.
 *
 * Before Sprint 02 the shell used a hardcoded `DEV_AUTH_USER` stub,
 * so every `/api/*` call went out unauthenticated and every module
 * silently rendered "0 of 0". See the Sprint 02 spec for the full
 * failure history.
 */

import { useQuery } from '@tanstack/react-query'
import { ApiError, jsonFetch } from '@/lib/api/jsonFetch'
import type { AuthUser } from '@/shell/module-system'
import type { Permission } from '@/shell/module-system'

/** Wire shape of `GET /api/auth/me`. Matches `middleware/app/api/routes/auth.py`
 *  — change here requires a middleware change too. */
interface AuthMeResponse {
  uid: number | string
  email: string
  name: string
  role: string
  pages: string[]
  can_chat: boolean
  can_confirm_actions: boolean
  /** Sprint 16 — true for outside_sales_rep / inside_sales_rep roles. */
  scoped?: boolean
  /** Sprint 16 — count of customers in the rep's scope. Null when unscoped. */
  scope_count?: number | null
  /** 2026-07-21 — Order History floor date (rbac.py `order_history_floor`),
   *  present only for roles that declare one (outside_sales_rep today). */
  order_history_floor?: string
  /** Connect-As — admin's identity when the SPA is in an impersonation
   *  session. Null when not impersonating. */
  impersonating?: {
    admin_email: string
    admin_name: string
    started_at: number
  } | null
}

function toAuthUser(payload: AuthMeResponse): AuthUser {
  // Build the Permission set from the server's role string + page list.
  // The manifest registry checks permissions with exact string matching,
  // so include both the role (e.g. 'admin') and every page key the user
  // can reach as-is. Modules declare `permissions: ['admin', 'sales', …]`,
  // matching this set.
  const set = new Set<Permission>()
  if (payload.role) set.add(payload.role as Permission)
  for (const page of payload.pages ?? []) set.add(page as Permission)
  return {
    uid: payload.uid,
    email: payload.email,
    name: payload.name,
    permissions: set,
    scoped: Boolean(payload.scoped),
    scopeCount:
      payload.scope_count === undefined ? null : payload.scope_count,
    orderHistoryFloor: payload.order_history_floor,
    impersonating: payload.impersonating
      ? {
          adminEmail: payload.impersonating.admin_email,
          adminName: payload.impersonating.admin_name,
          startedAt: payload.impersonating.started_at,
        }
      : null,
  }
}

/** Fetches the current signed-in user's identity from the server. Exported
 *  so callers that just flipped identity server-side (Connect-As, exit
 *  impersonation) can read the fresh permissions before deciding where to
 *  navigate — the caller's own React state still holds the pre-mutation
 *  identity at that point. */
export async function fetchCurrentAuthUser(): Promise<AuthUser> {
  const payload = await jsonFetch<AuthMeResponse>('/api/auth/me')
  return toAuthUser(payload)
}

/** Fetch + cache the current signed-in user. `isAuthenticated` derives
 *  directly from the query state so callers don't have to juggle both
 *  error and data checks.
 *
 *  - 401 → error is an `ApiError` with `isAuthError === true`; render
 *    the login screen.
 *  - Network / 5xx → error is `ApiError` with some other status; show
 *    a generic banner and a retry.
 *  - 200 → `user` is populated.
 *
 *  Staleness is 5 minutes — identity doesn't change within a session. */
export function useCurrentUser() {
  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchCurrentAuthUser,
    retry: (failureCount, error) => {
      // Don't retry on 401 — that's a signal, not a transient glitch.
      if (error instanceof ApiError && error.isAuthError) return false
      return failureCount < 2
    },
    staleTime: 5 * 60_000,
    // Re-validate when the user returns to the tab. Mid-session cookie
    // expiry then routes back to LoginScreen without every module needing
    // its own ApiErrorBoundary. See Sprint 02 "During-sprint decisions".
    refetchOnWindowFocus: true,
  })

  const isAuthError = query.error instanceof ApiError && query.error.isAuthError

  return {
    user: query.data,
    isLoading: query.isLoading,
    isAuthError,
    error: !isAuthError ? query.error : null,
    refetch: query.refetch,
  }
}

/** Browser navigation to the middleware OAuth login. The server handles
 *  the Google round-trip + sets the `carla_ui_session` cookie + redirects
 *  the browser back to `/`. */
export function redirectToLogin(): void {
  // Preserve the user's intended destination so we can send them back after
  // login. sessionStorage survives the Google redirect; localStorage would
  // leak across tabs.
  if (typeof window !== 'undefined' && window.location.pathname !== '/') {
    try {
      sessionStorage.setItem(
        'spi-ops:post-login-path',
        `${window.location.pathname}${window.location.search}`,
      )
    } catch {
      // sessionStorage may be unavailable in some embedded contexts — fall
      // through, the user just lands on `/` after login.
    }
  }
  window.location.assign('/api/auth/login')
}

/** Browser navigation to the middleware logout route. The server clears
 *  the session cookie (and the impersonation cookie, if present) and
 *  redirects back to `/api/auth/login`. A hard navigation — not a fetch —
 *  mirrors `redirectToLogin()` so the Set-Cookie/redirect chain applies
 *  cleanly and all in-memory app state resets on the next load. */
export function logout(): void {
  window.location.assign('/api/auth/logout')
}

/** Call on app mount once the user is authenticated. Pops any saved
 *  post-login path from sessionStorage and navigates there. Returns the
 *  path (or null) so callers can choose to replace history. */
export function consumePostLoginPath(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const path = sessionStorage.getItem('spi-ops:post-login-path')
    if (path) sessionStorage.removeItem('spi-ops:post-login-path')
    return path
  } catch {
    return null
  }
}
